const getSpace = require('./contentful/get-space');
const transformer = require('./transformers/asset');
const processError = require('./contentful/error-process');
const {updateAsset, publishAsset, unpublishAsset, archiveAsset, unarchiveAsset} = require('./contentful/asset');
const config = require('../../config/config');

const ITEMS_PER_PAGE = 100;

const servicesSettings = config.services;
const servicesBasePath = `${servicesSettings.MG_PROTOCOL}://${servicesSettings.MG_DOMAIN}:${servicesSettings.MG_PORT}${servicesSettings.MG_SERVICES_BASE}`;

module.exports = {
  getById(assetId) {
    if (!assetId) {
      throw new Error('Expected non-falsy asset ID.');
    }

    return getSpace().then((space) => {
      return space.getAsset(
        assetId
      ).catch(
        processError
      ).then((asset) => {
        return transformer.contentfulToInternal(asset);
      });
    });
  },

  allByParams(offset = 0, limit = ITEMS_PER_PAGE, params = {}, order) {
    return getSpace().then((space) => {
      const requestParams = {
        order: order || '-sys.updatedAt',
        skip: offset,
        limit: limit
      };

      if (!params.includeArchived) {
        requestParams['sys.archivedAt[exists]'] = false;
      }

      if (params.contentType) {
        requestParams['fields.file.contentType'] = params.contentType;
      }

      if (params.searchFilter) {
        requestParams['query'] = params.searchFilter;
      }

      if (params.mimetypeGroup) {
        requestParams['mimetype_group'] = params.mimetypeGroup;
      }

      if (params.tags) {
        requestParams['metadata.tags.sys.id[in]'] = params.tags;
      }

      return space.getAssets(requestParams).catch(
        processError
      ).then((assets) => {
        return [assets.items.map(transformer.contentfulToInternal), assets.total];
      });
    });
  },

  save(data) {
    if (!data.id) {
      throw new Error('Expected non-falsy asset ID.');
    }

    return getSpace().then((space) => {
      return space.getAsset(
        data.id
      ).catch(
        processError
      ).then((asset) => {
        return updateAsset(asset, transformer.internalToContentful(data));
      }).then((asset) => {
        return publishAsset(asset);
      }).then((asset) => {
        return transformer.contentfulToInternal(asset);
      });
    });
  },

  search(config) {
    config = Object.assign({
      query: '',
      limit: 10,
      offset: 0,
      contentType: null,
      tags: []
    }, config || {});

    return getSpace().then((space) => {
      const options = {
        'query': config.query,
        'skip': config.offset,
        'limit': config.limit,
        'sys.archivedAt[exists]': false,
        'order': '-sys.updatedAt'
      };

      if (config.tags.length > 0) {
        options['metadata.tags.sys.id[all]'] = config.tags.join(',');
      }

      if (config.mimetype) {
        switch (config.mimetype) {
          case 'image/*':
            options.mimetype_group = 'image';
            break;
          case 'video/*':
            options.mimetype_group = 'video';
            break;
          case 'application/pdf':
            options.mimetype_group = 'pdfdocument';
            break;
          default:
            throw new Error(`Unsupported mimetype ${config.mimetype}.`);
        }
      }

      return space.getAssets(
        options
      ).catch(
        processError
      ).then((assets) => {
        return {
          items: assets.items.map(transformer.contentfulToInternal),
          limit: assets.limit,
          offset: assets.skip,
          total: assets.total
        };
      });
    });
  },

  upload(file, tagIDs = []) {
    return getSpace().then((space) => {
      return space.createAssetFromFiles(
        {
          fields: {
            file: {
              'nb-NO': {
                contentType: file.contentType,
                fileName: file.filename,
                file: file.content
              }
            },
            title: {
              'nb-NO': file.filename
            }
          },
          metadata: {
            tags: tagIDs.map((tagID) => {
              return {
                sys: {
                  type: 'Link',
                  linkType: 'Tag',
                  id: tagID
                }
              };
            })
          }
        }
      ).catch(
        processError
      );
    }).then((asset) => {
      return asset.processForAllLocales({
        processingCheckWait: 500,
        processingCheckRetries: 40
      }).catch(
        processError
      );
    }).then((asset) => {
      return publishAsset(asset);
    }).then((asset) => {
      return transformer.contentfulToInternal(asset);
    });
  },

  publish(id) {
    return getSpace().then((space) => {
      return space.getAsset(id).then((asset) => {
        return publishAsset(asset);
      }).then((asset) => {
        return transformer.contentfulToInternal(asset);
      });
    });
  },

  unpublish(id) {
    return getSpace().then((space) => {
      return space.getAsset(id).then((asset) => {
        return unpublishAsset(asset);
      }).then((asset) => {
        return transformer.contentfulToInternal(asset);
      });
    });
  },

  archive(id) {
    return getSpace().then((space) => {
      return space.getAsset(id).then((asset) => {
        return archiveAsset(asset);
      }).then((asset) => {
        return transformer.contentfulToInternal(asset);
      });
    });
  },

  unarchive(id) {
    return getSpace().then((space) => {
      return space.getAsset(id).then((asset) => {
        return unarchiveAsset(asset);
      }).then((asset) => {
        return publishAsset(asset);
      }).then((asset) => {
        return transformer.contentfulToInternal(asset);
      });
    });
  },

  getLinkedEntries(assetID) {
    if (!assetID) {
      throw new Error('Expected non-falsy asset ID.');
    }

    return getSpace().then((space) => {
      return space.getEntries({
        links_to_asset: assetID
      }).catch(
        processError
      ).then((entries) => {
        const data = entries.toPlainObject();

        return data.items;
      });
    });
  },

  async getAssetStatus(assetIDs) {
    try {
      const res = await fetch(`${servicesBasePath}assets/status`, {
        method: 'POST',
        body: JSON.stringify({
          ids: assetIDs
        })
      });

      if (!res.ok) {
        throw new Error(`non-OK status returned from services. Status: ${res.status}`);
      }

      return res.json();
    } catch (error) {
      console.error(error);
      return {};
    }
  }
};
